/*
Component "last-view-products"
Description
  A web component for displaying a list of recently viewed products on a Shopify store page.
  Automatically loads data from localStorage, renders product cards, and shows a message if the list is empty.

Usage:

Add the component to the page:
  <last-view-products
    data-section-id="sectionId"
    data-message-if-empty="You haven't viewed any products yet."
  >
    <template id="template-last-view-products--{{section.id}}">
      <div class="js-product-list"></div>
    </template>
  </last-view-products>


Parameters:
  data-section-id — section identifier for the spinner and container.
  data-message-if-empty — message displayed when there are no viewed products.

Data storage:
  The product list is taken from localStorage under the key data_last_viewed_products. An array of objects with the handle field is expected.

Component behavior:
  - When it appears in the viewport (IntersectionObserver), the component loads product cards via AJAX and renders them.
  - If there are no products, a message is displayed.

*** IMPORTANT ***
  - For correct operation, a template with the ".js-product-list" class must be inside the component.
  - Use class SaveLastProducts from file save-last-products.js to save viewed products to localStorage.
* */

class LastViewProducts extends HTMLElement {
  constructor() {
    super();
    this.constants = {
      DATA_KEY: 'data_last_viewed_products'
    };

    this.variables = {
      sectionId: this.dataset.sectionId || '',
      messageIfEmpty: this.dataset.messageIfEmpty || 'You haven\'t viewed any products yet.'
    };

    this.lastViewedProducts = this.dataFromStorage;
    this.productCards = [];
  }

  get dataFromStorage() {
    const data = localStorage.getItem(this.constants.DATA_KEY);
    if (!data) return [];

    return JSON.parse(data);
  }

  async getProduct(handle) {
    const url = `/products/${handle}?sections=product-card`;

    try {
      const response = await fetch(url);
      if (!response.ok) throw new Error(`Could not fetch ${url}, status: ${response.status}`);
      const data = await response.json();
      return data['product-card'];

    } catch (err) {
      console.log(err);
    }
  }

  async loadProducts() {
    const productPromises = await Promise.allSettled(
      this.lastViewedProducts.map(async item => await this.getProduct(item.handle))
    );

    this.productCards = this.parseProductCardResponse(productPromises);
  }

  parseProductCardResponse(data) {
    return data.filter(item => item.status === 'fulfilled')
      .map(item => {
        const html = new DOMParser().parseFromString(item.value, 'text/html');
        return html.querySelector('product-card');
      });
  }

  renderMarkup() {
    const { sectionId, messageIfEmpty } = this.variables;
    const countVisibleProducts = this.dataset.visibleProducts;

    const template = this.querySelector(`#template-last-view-products--${sectionId}`)?.content.cloneNode(true);
    const scrollbar = template ? template.querySelector('.scrollbar') : null;
    let targetContainer = template ? template.querySelector('.js-product-list') : null;

    if (!targetContainer) {
      targetContainer = this;
    }

    if (this.productCards.length === 0) {
      this.insertAdjacentHTML(
        'beforebegin',
        `<p style="margin: 20px" class="h3 text-center">${messageIfEmpty}</p>`
      );

      return;
    }

    this.productCards.forEach(card => {
      targetContainer.insertAdjacentElement('beforeend', card);
    });

    if (countVisibleProducts && scrollbar && (this.productCards.length > countVisibleProducts)) {
      scrollbar.classList.remove('peer-not-scrollable:hidden');
    }

    if (template) {
      this.append(template);
    }
  }

  async render() {
    const { sectionId } = this.variables;
    await this.loadProducts();
    this.renderMarkup();
    Shopify.UIHelpers?.spinner?.hidden(`.js-last-view-products--${sectionId}`);
  }

  connectedCallback() {
    Shopify.UIHelpers?.spinner?.show(`.js-last-view-products--${this.variables.sectionId}`);

    const observer = new IntersectionObserver((entries, obs) => {
      if (entries[0].isIntersecting) {
        obs.disconnect();
        (async () => await this.render())();
      }
    }, { threshold: 0.1 });

    observer.observe(this);
  }
}

customElements.define('last-view-products', LastViewProducts);